/**
 * Normalise une chaîne de caractères : minuscules, suppression des accents et espaces superflus.
 */
export function normalizeString(str) {
  if (!str) return "";
  return String(str)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

/**
 * Normalise la forme juridique d'une entité.
 * Renvoie une clé canonique : "SPFPL", "SAS", "SARL", "SCI" ou la chaîne normalisée.
 */
export function normalizeLegalForm(forme) {
  if (!forme) return "";
  const cleaned = normalizeString(forme).toUpperCase();

  // SPFPL en premier pour éviter que "SPFPL SAS" ne soit catégorisé en SAS
  if (cleaned.includes("SPFPL")) return "SPFPL";
  if (
    cleaned.includes("SASU") ||
    cleaned === "SAS" ||
    cleaned.startsWith("SAS ") ||
    cleaned.endsWith(" SAS") ||
    cleaned.includes("ACTIONS SIMPLIFIEE")
  ) return "SAS";
  if (
    cleaned.includes("EURL") ||
    cleaned === "SARL" ||
    cleaned.startsWith("SARL ") ||
    cleaned.endsWith(" SARL") ||
    cleaned.includes("RESPONSABILITE LIMITEE")
  ) return "SARL";
  if (
    cleaned === "SCI" ||
    cleaned.startsWith("SCI ") ||
    cleaned.endsWith(" SCI") ||
    cleaned.includes("CIVILE IMMOBILIERE")
  ) return "SCI";

  return cleaned;
}

/**
 * Vérifie si une société correspond au filtre juridique sélectionné.
 */
export function matchLegalForm(soc, selectedForm) {
  if (!selectedForm || selectedForm === "TOUS") return true;

  if (selectedForm === "HOLDINGS") {
    return soc.holding === true;
  }

  const rawForm = soc.formeJuridique || soc.statutJuridique || "";
  const normalized = normalizeLegalForm(rawForm);

  if (selectedForm === "SPFPL") {
    return normalized === "SPFPL";
  }
  if (selectedForm === "SAS" || selectedForm === "SAS/SASU") {
    return normalized === "SAS";
  }
  if (selectedForm === "SARL" || selectedForm === "SARL/EURL") {
    return normalized === "SARL";
  }
  if (selectedForm === "SCI") {
    return normalized === "SCI";
  }

  return normalized === selectedForm;
}

/**
 * Vérifie si une société correspond au terme de recherche.
 * Porte sur dénomination/raison sociale, SIREN et SIRET séparément, ville,
 * et identité du dirigeant uniquement si renseignée.
 */
export function matchSearch(soc, searchTerm) {
  if (!searchTerm || !searchTerm.trim()) return true;

  const q = normalizeString(searchTerm);

  // 1. Dénomination / Raison sociale
  const name = normalizeString(soc.denomination || soc.raisonSociale || soc.nom || "");
  if (name.includes(q)) return true;

  // 2. SIREN et SIRET nettoyés de tout espace
  const qDigits = q.replace(/\s/g, "");
  const siren = String(soc.siren || "").replace(/\s/g, "");
  const siret = String(soc.siret || "").replace(/\s/g, "");
  if (qDigits && ((siren && siren.includes(qDigits)) || (siret && siret.includes(qDigits)))) {
    return true;
  }

  // 3. Ville
  const ville = normalizeString(soc.ville || "");
  if (ville.includes(q)) return true;

  // 4. Dirigeant (uniquement si réellement renseigné)
  if (soc.gerant && (soc.gerant.nom || soc.gerant.prenom)) {
    const gerantFullName = normalizeString(`${soc.gerant.prenom || ""} ${soc.gerant.nom || ""}`);
    if (gerantFullName.includes(q)) return true;
  }

  return false;
}

/**
 * Filtre la collection complète selon la recherche textuelle et la forme juridique.
 */
export function filterSocietes(societes = [], searchTerm = "", selectedForm = "TOUS") {
  if (!Array.isArray(societes)) return [];

  return societes.filter((soc) => {
    return matchSearch(soc, searchTerm) && matchLegalForm(soc, selectedForm);
  });
}
